import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import {
	METHODIC_REFRESH_REQUEST_EVENT,
	METHODIC_SESSION_UPDATED_EVENT,
} from '@liquidweb-dev/lw-chat-js';

import { useOidcSilent } from 'modules/oidc/silentProvider';

/**
 * Bridges the Methodic widget's session-refresh protocol to the portal's
 * OIDC renewal path, and announces portal-originated identity changes
 * (sign-out, account switch, masquerade toggle) so an open widget
 * reconciles immediately.
 * @param enabled whether the bridge should be active
 */
export default function usePortalSessionBridge(enabled: boolean): void {
	const [, silentRefresh] = useOidcSilent();
	const { user } = useAuth();
	const subjectRef = useRef<string | undefined>(user?.profile?.sub);

	useEffect(() => {
		if (!enabled) return undefined;

		const controller = new AbortController();

		const handleRefreshRequest = () => {
			silentRefresh({ signal: controller.signal })
				.then((refreshedUser) => {
					if (controller.signal.aborted || !refreshedUser) return;

					window.dispatchEvent(new Event(METHODIC_SESSION_UPDATED_EVENT));
				})
				.catch((error) => {
					if (controller.signal.aborted) return;

					// eslint-disable-next-line no-console
					console.error(
						new Error('Methodic portal-session renewal failed', {
							cause: error,
						}),
					);
				});
		};

		window.addEventListener(
			METHODIC_REFRESH_REQUEST_EVENT,
			handleRefreshRequest,
		);

		return () => {
			controller.abort();
			window.removeEventListener(
				METHODIC_REFRESH_REQUEST_EVENT,
				handleRefreshRequest,
			);
		};
	}, [enabled, silentRefresh]);

	useEffect(() => {
		if (!enabled) return;

		const currentSubject = user?.profile?.sub;
		if (currentSubject === subjectRef.current) return;

		subjectRef.current = currentSubject;
		window.dispatchEvent(new Event(METHODIC_SESSION_UPDATED_EVENT));
	}, [enabled, user?.profile?.sub]);
}
